import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, Box, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Snackbar, Alert, Switch,
  FormControlLabel, Chip, FormGroup, Checkbox, Grid, Select, MenuItem,
  FormControl, InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, userId: null, userName: '' });
  const { getAuthHeaders, isAdmin } = useAuth();
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    fullName: '',
    role: 'User',
    isActive: true,
    permission: {
      canManageDrivers: false,
      canManageVehicles: false,
      canUploadVehicles: false,
      canManageRoutes: false,
      canManageTrips: false,
      canUploadTrips: false,
      canUploadTemplates: false,
      canAssignTemplates: false,
      canUploadPastDates: false
    }
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/Users', { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
      setSnackbar({ open: true, message: 'Hiba a felhasználók betöltésekor', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditMode(true);
      setSelectedUser(user);
      setFormData({
        username: user.username,
        password: '',
        email: user.email || '',
        fullName: user.fullName || '',
        role: user.role,
        isActive: user.isActive,
        permission: user.permission || {
          canManageDrivers: false,
          canManageVehicles: false,
          canUploadVehicles: false,
          canManageRoutes: false,
          canManageTrips: false,
          canUploadTrips: false,
          canUploadTemplates: false,
          canAssignTemplates: false,
          canUploadPastDates: false
        }
      });
    } else {
      setEditMode(false);
      setSelectedUser(null);
      setFormData({
        username: '',
        password: '',
        email: '',
        fullName: '',
        role: 'User',
        isActive: true,
        permission: {
          canManageDrivers: false,
          canManageVehicles: false,
          canUploadVehicles: false,
          canManageRoutes: false,
          canManageTrips: false,
          canUploadTrips: false,
          canUploadTemplates: false,
          canAssignTemplates: false,
          canUploadPastDates: false
        }
      });
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedUser(null);
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      permission: {
        ...prev.permission,
        [name]: checked
      }
    }));
  };

  const handleRoleChange = (role) => {
    let permissions = { ...formData.permission };
    
    if (role === 'Admin') {
      permissions = {
        canManageDrivers: true,
        canManageVehicles: true,
        canUploadVehicles: true,
        canManageRoutes: true,
        canManageTrips: true,
        canUploadTrips: true,
        canUploadTemplates: true,
        canAssignTemplates: true,
        canUploadPastDates: true
      };
    } else if (role === 'DataEntry') {
      permissions = {
        canManageDrivers: true,
        canManageVehicles: true,
        canUploadVehicles: true,
        canManageRoutes: true,
        canManageTrips: true,
        canUploadTrips: true,
        canUploadTemplates: true,
        canAssignTemplates: true,
        canUploadPastDates: false
      };
    } else if (role === 'Viewer') {
      permissions = {
        canManageDrivers: false,
        canManageVehicles: false,
        canUploadVehicles: false,
        canManageRoutes: false,
        canManageTrips: false,
        canUploadTrips: false,
        canUploadTemplates: false,
        canAssignTemplates: false,
        canUploadPastDates: false
      };
    }

    setFormData(prev => ({
      ...prev,
      role,
      permission: permissions
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.username || (!editMode && !formData.password)) {
        setSnackbar({ open: true, message: 'Kérem töltse ki a kötelező mezőket', severity: 'error' });
        return;
      }

      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      };

      if (editMode) {
        await axios.put(`/api/Users/${selectedUser.id}`, formData, { headers });
        setSnackbar({ open: true, message: 'Felhasználó sikeresen frissítve', severity: 'success' });
      } else {
        await axios.post('/api/Users', formData, { headers });
        setSnackbar({ open: true, message: 'Felhasználó sikeresen létrehozva', severity: 'success' });
      }

      handleCloseDialog();
      loadUsers();
    } catch (err) {
      console.error('Error saving user:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Hiba a felhasználó mentésekor', 
        severity: 'error' 
      });
    }
  };

  const handleDeleteClick = (user) => {
    setConfirmDialog({
      open: true,
      userId: user.id,
      userName: user.username
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/Users/${confirmDialog.userId}`, { headers: getAuthHeaders() });
      setSnackbar({ open: true, message: 'Felhasználó sikeresen törölve', severity: 'success' });
      loadUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Hiba a felhasználó törlésekor', 
        severity: 'error' 
      });
    } finally {
      setConfirmDialog({ open: false, userId: null, userName: '' });
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'Admin': return 'Adminisztrátor';
      case 'DataEntry': return 'Adatrögzítő';
      case 'Viewer': return 'Megtekintő';
      default: return 'Felhasználó';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return 'error';
      case 'DataEntry': return 'primary';
      case 'Viewer': return 'default';
      default: return 'default';
    }
  };

  // Ha nem admin, nem engedjük hozzáférni
  if (!isAdmin()) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">
            Nincs jogosultsága a felhasználók kezeléséhez. Csak adminisztrátorok férhetnek hozzá ehhez az oldalhoz.
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Felhasználók kezelése
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Új felhasználó
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Felhasználónév</TableCell>
                <TableCell>Teljes név</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Szerepkör</TableCell>
                <TableCell>Állapot</TableCell>
                <TableCell>Utolsó belépés</TableCell>
                <TableCell align="right">Műveletek</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Betöltés...</TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Nincsenek felhasználók</TableCell>
                </TableRow>
              ) : (
                users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.fullName || '-'}</TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getRoleLabel(user.role)} 
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.isActive ? 'Aktív' : 'Inaktív'} 
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleString('hu-HU') : 'Soha'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        color="primary"
                        onClick={() => handleOpenDialog(user)}
                        title="Szerkesztés"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error"
                        onClick={() => handleDeleteClick(user)}
                        title="Törlés"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Felhasználó szerkesztő dialógus */}
      <Dialog 
        open={showDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Felhasználó szerkesztése' : 'Új felhasználó létrehozása'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Felhasználónév"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={editMode ? 'Új jelszó (opcionális)' : 'Jelszó'}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  fullWidth
                  required={!editMode}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Teljes név"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Szerepkör</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    label="Szerepkör"
                  >
                    <MenuItem value="Admin">Adminisztrátor</MenuItem>
                    <MenuItem value="DataEntry">Adatrögzítő</MenuItem>
                    <MenuItem value="Viewer">Megtekintő</MenuItem>
                    <MenuItem value="User">Felhasználó</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      name="isActive"
                    />
                  }
                  label="Aktív felhasználó"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Jogosultságok
                </Typography>
                <FormGroup>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permission.canManageDrivers}
                            onChange={handlePermissionChange}
                            name="canManageDrivers"
                          />
                        }
                        label="Sofőrök kezelése"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permission.canManageVehicles}
                            onChange={handlePermissionChange}
                            name="canManageVehicles"
                          />
                        }
                        label="Járművek kezelése (lekérdezés)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permission.canUploadVehicles}
                            onChange={handlePermissionChange}
                            name="canUploadVehicles"
                          />
                        }
                        label="Járművek feltöltése / szerkesztése"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permission.canManageRoutes}
                            onChange={handlePermissionChange}
                            name="canManageRoutes"
                          />
                        }
                        label="Vonalak kezelése"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permission.canManageTrips}
                            onChange={handlePermissionChange}
                            name="canManageTrips"
                          />
                        }
                        label="Járatok kezelése (lekérdezés)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permission.canUploadTrips}
                            onChange={handlePermissionChange}
                            name="canUploadTrips"
                          />
                        }
                        label="Járatok betöltése"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permission.canUploadTemplates}
                            onChange={handlePermissionChange}
                            name="canUploadTemplates"
                          />
                        }
                        label="Sablonok feltöltése"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permission.canAssignTemplates}
                            onChange={handlePermissionChange}
                            name="canAssignTemplates"
                          />
                        }
                        label="Sablonok hozzárendelése"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permission.canUploadPastDates}
                            onChange={handlePermissionChange}
                            name="canUploadPastDates"
                          />
                        }
                        label="Múltbeli dátumokra feltöltés (csak Admin)"
                      />
                    </Grid>
                  </Grid>
                </FormGroup>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Mégsem</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editMode ? 'Mentés' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Törlés megerősítő dialógus */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, userId: null, userName: '' })}
      >
        <DialogTitle>Felhasználó törlése</DialogTitle>
        <DialogContent>
          <Typography>
            Biztosan törölni szeretné a(z) "{confirmDialog.userName}" felhasználót?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, userId: null, userName: '' })}>
            Mégsem
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar értesítések */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Users;
